import * as React from "react"
import { ChevronDown, ChevronUp, ClipboardList } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import type {
    FieldValueDTO,
    PatchClientBlock,
    PatchDropdownItem,
    PatchRequest,
    VisitCardDTO,
} from "@/api/visit_card_client"
import { Combobox, type ComboboxItem } from "@/components/ui/combobox"
import { ResizeHandle } from "@/components/ui/resize-handle"
import type { ReferencesMap } from "../../hooks/useVisitCard"
import {
    MAIN_TEXT_FIELDS,
    buildSubmitPayload,
    getField,
    type MainTextFieldSpec,
    type VisitCardSubmitPayload,
} from "./submitPayload"


const MIN_HEIGHT = 160
const MAX_HEIGHT = 600
/** Без футера Axapta: шапка + табы + 3 ряда полей. */
const DEFAULT_HEIGHT = 310
const COLLAPSED_HEIGHT = 40


interface DropdownSpec {
    flatKey: string
    path: readonly string[]
    label: string
    dictKey: string
}

const DROPDOWNS_CLIENT: readonly DropdownSpec[] = [
    { flatKey: "client__juridical_type", path: ["client", "juridical_type"], label: "Тип клиента", dictKey: "juridical-types" },
    { flatKey: "client__social_status", path: ["client", "social_status"], label: "Статус клиента", dictKey: "social-statuses" },
] as const

type TabKey = "client" | "main"

const TABS: { id: TabKey; label: string }[] = [
    { id: "client", label: "Клиент" },
    { id: "main", label: "Основное" },
]

const GRID_ROW_2 = "grid grid-cols-2 gap-x-3 gap-y-2"
const GRID_ROW_3 = "grid grid-cols-2 gap-x-3 gap-y-2 lg:grid-cols-3"
const GRID_ROW_4 = "grid grid-cols-2 gap-x-3 gap-y-2 lg:grid-cols-4"


export interface VisitCardBottomPanelProps {
    sessionId: string | null
    card: VisitCardDTO | null
    references: ReferencesMap
    referencesError?: string | null
    onRetryReferences?: () => void
    flashes: string[]
    /** Mutation error from useVisitCard (patch onError). */
    actionError?: string | null
    onClearActionError?: () => void
    onPatch: (sessionId: string, patch: PatchRequest) => Promise<void>
    onSubmit?: (sessionId: string, payload: VisitCardSubmitPayload) => Promise<unknown>
    isSubmitting?: boolean
    profileName?: string
    className?: string
    registerTourTarget?: (id: string, el: HTMLElement | null) => void
}


export function VisitCardBottomPanel({
    sessionId,
    card,
    references,
    referencesError,
    onRetryReferences,
    flashes,
    actionError = null,
    onClearActionError,
    onPatch,
    onSubmit,
    isSubmitting = false,
    profileName,
    className,
    registerTourTarget,
}: VisitCardBottomPanelProps) {
    const [isCollapsed, setIsCollapsed] = React.useState(false)
    const [panelHeight, setPanelHeight] = React.useState(DEFAULT_HEIGHT)
    const [isDragging, setIsDragging] = React.useState(false)
    const [activeTab, setActiveTab] = React.useState<TabKey>("main")
    /** Local overrides for Main tab text fields (including salon). */
    const [mainLocal, setMainLocal] = React.useState<Record<string, string>>({})
    /** Fingerprint of last successfully submitted payload; null until a dump succeeds. */
    const [submittedFingerprint, setSubmittedFingerprint] = React.useState<string | null>(null)

    React.useEffect(() => {
        setMainLocal({})
        setSubmittedFingerprint(null)
    }, [sessionId])

    const panelHeightRef = React.useRef(panelHeight)
    React.useEffect(() => {
        panelHeightRef.current = panelHeight
    }, [panelHeight])

    const panelRef = React.useCallback(
        (el: HTMLDivElement | null) => {
            registerTourTarget?.("visit-card", el)
        },
        [registerTourTarget],
    )

    const handleDragStart = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        const startY = e.clientY
        const startHeight = panelHeightRef.current
        setIsDragging(true)
        const onMouseMove = (ev: MouseEvent) => {
            const delta = startY - ev.clientY
            const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeight + delta))
            setPanelHeight(newHeight)
        }
        const onMouseUp = () => {
            setIsDragging(false)
            window.removeEventListener("mousemove", onMouseMove)
            window.removeEventListener("mouseup", onMouseUp)
        }
        window.addEventListener("mousemove", onMouseMove)
        window.addEventListener("mouseup", onMouseUp)
    }, [])

    const tabFlashCounts = React.useMemo(() => {
        const counts: Record<TabKey, number> = { client: 0, main: 0 }
        if (!flashes.length) return counts
        for (const flat of flashes) {
            if (flat.startsWith("client__")) counts.client++
            else if (!flat.startsWith("funnel__")) counts.main++
        }
        return counts
    }, [flashes])

    if (sessionId === null) return null

    const currentPayload = card ? buildSubmitPayload(card, mainLocal, profileName) : null
    const fingerprint = currentPayload ? JSON.stringify(currentPayload) : null
    const isSubmitted = fingerprint !== null && fingerprint === submittedFingerprint

    const runPatch = (patch: PatchRequest) => {
        onClearActionError?.()
        void onPatch(sessionId, patch)
    }

    const handleClientText = (attr: keyof PatchClientBlock, value: string) => {
        runPatch({ client: { [attr]: value } as PatchClientBlock })
    }

    const handleDropdown = (flatKey: string, item: ComboboxItem | null) => {
        const payload: PatchDropdownItem | null = item ? { id: item.id, name: item.name } : null
        runPatch({ dropdowns: { [flatKey]: payload } })
    }

    const handleComments = (value: string) => {
        runPatch({ comments: value })
    }

    const handleMainLocalChange = (flatKey: string, value: string) => {
        setMainLocal((prev) => ({ ...prev, [flatKey]: value }))
    }

    const handleSubmit = () => {
        if (!card || !onSubmit || !currentPayload) return
        onClearActionError?.()
        const payload = currentPayload
        void (async () => {
            const result = await onSubmit(sessionId, payload)
            if (result != null) {
                setSubmittedFingerprint(JSON.stringify(payload))
            }
        })()
    }

    return (
        <div
            ref={panelRef}
            data-tour-target="visit-card"
            className={cn(
                "flex flex-col rounded-l border border-neutral-border-300 bg-neutral-background-200 overflow-hidden",
                !isDragging && "transition-[height] duration-200",
                className,
            )}
            style={{ height: isCollapsed ? `${COLLAPSED_HEIGHT}px` : `${panelHeight}px` }}
        >
            {!isCollapsed && <ResizeHandle onMouseDown={handleDragStart} />}

            {/* header */}
            <div className="flex h-9 shrink-0 items-center justify-between gap-2 px-3 border-b border-neutral-border-300">
                <div className="flex items-center gap-2 min-w-0">
                    <ClipboardList className="size-3.5 text-primary-400 dark:text-primary-600 shrink-0" strokeWidth={2} />
                    <span className="text-body2 font-medium text-neutral-text-900">Карточка обращения</span>
                    {card && (
                        <>
                            <span className="text-neutral-text-400">·</span>
                            <span className="text-neutral-text-800 truncate">
                                {[card.client?.last_name, card.client?.first_name].filter(Boolean).join(" ") || "—"}
                            </span>
                            {card.client?.is_new ? (
                                <span className="text-caption text-state-positive shrink-0 px-1.5 rounded-full bg-state-positive/10">новый</span>
                            ) : (
                                <span className="text-caption text-state-positive shrink-0 px-1.5 rounded-full bg-state-positive/10">постоянный</span>
                            )}
                        </>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0 self-center">
                    <Button
                        size="sm"
                        disabled={isSubmitting || isSubmitted || !card || !onSubmit}
                        onClick={handleSubmit}
                        className="-translate-y-1"
                    >
                        {isSubmitting ? "Отправка…" : isSubmitted ? "Отправлено" : "Отправить"}
                    </Button>
                    <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => setIsCollapsed((c) => !c)}
                        aria-label={isCollapsed ? "Развернуть" : "Свернуть"}
                    >
                        {isCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </Button>
                </div>
            </div>

            {!isCollapsed && card && (
                <>
                    {/* tabs */}
                    <Tabs
                        value={activeTab}
                        onValueChange={(value) => setActiveTab(value as typeof activeTab)}
                        className="shrink-0"
                    >
                        <TabsList>
                            {TABS.map((tab) => {
                                const flashCount = tabFlashCounts[tab.id]
                                return (
                                    <TabsTrigger
                                        key={tab.id}
                                        value={tab.id}
                                        badge={
                                            flashCount > 0 ? (
                                                <span className="rounded-full bg-primary-500 text-neutral-white text-caption px-1.5 leading-none py-0.5">
                                                    {flashCount}
                                                </span>
                                            ) : null
                                        }
                                    >
                                        {tab.label}
                                    </TabsTrigger>
                                )
                            })}
                        </TabsList>
                    </Tabs>

                    {referencesError && (
                        <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-1.5 text-caption text-state-warning bg-state-warning/10 border-b border-neutral-border-300">
                            <span className="truncate" title={referencesError}>⚠ {referencesError}</span>
                            {onRetryReferences && (
                                <Button size="xs" variant="ghost" onClick={onRetryReferences} className="shrink-0">
                                    Повторить
                                </Button>
                            )}
                        </div>
                    )}

                    <div className="flex-1 overflow-hidden px-3 py-2">
                        {activeTab === "client" && (
                            <ClientTab
                                card={card}
                                references={references}
                                flashes={flashes}
                                onClientText={handleClientText}
                                onDropdown={handleDropdown}
                            />
                        )}
                        {activeTab === "main" && (
                            <MainTab
                                card={card}
                                flashes={flashes}
                                mainLocal={mainLocal}
                                onMainLocalChange={handleMainLocalChange}
                                onComments={handleComments}
                            />
                        )}
                    </div>

                    {actionError && (
                        <div className="shrink-0 px-3 py-1.5 text-caption text-state-negative bg-state-negative/10 border-t border-neutral-border-300">
                            {actionError}
                        </div>
                    )}
                </>
            )}

            {!isCollapsed && !card && (
                <div className="flex-1 flex flex-col items-center justify-center gap-2 text-body2 text-neutral-text-500">
                    <ClipboardList className="size-6 text-neutral-text-400" />
                    <p>Карточка ещё не инициализирована</p>
                    <p className="text-caption">Начните копилот-сессию — карточка появится автоматически</p>
                </div>
            )}
        </div>
    )
}


// ─────────────────────────────────────────────────────────────────────────────
// Tab content


function ClientTab({
    card,
    references,
    flashes,
    onClientText,
    onDropdown,
}: {
    card: VisitCardDTO
    references: ReferencesMap
    flashes: string[]
    onClientText: (attr: keyof PatchClientBlock, value: string) => void
    onDropdown: (flatKey: string, item: ComboboxItem | null) => void
}) {
    const juridical = DROPDOWNS_CLIENT.find((s) => s.flatKey === "client__juridical_type")!
    const social = DROPDOWNS_CLIENT.find((s) => s.flatKey === "client__social_status")!
    const juridicalField = getField(card, juridical.path) ?? EMPTY_FIELD
    const socialField = getField(card, social.path) ?? EMPTY_FIELD
    return (
        <div className="space-y-2">
            <div className={GRID_ROW_4}>
                <FieldCell label="Фамилия">
                    <EditableClientInput value={card.client?.last_name ?? ""} onCommit={(v) => onClientText("last_name", v)} />
                </FieldCell>
                <FieldCell label="Имя">
                    <EditableClientInput value={card.client?.first_name ?? ""} onCommit={(v) => onClientText("first_name", v)} />
                </FieldCell>
                <FieldCell label="Отчество">
                    <EditableClientInput value={card.client?.patronymic ?? ""} onCommit={(v) => onClientText("patronymic", v)} />
                </FieldCell>
                <FieldCell label="Телефон">
                    <EditableClientInput value={card.client?.phone ?? ""} onCommit={(v) => onClientText("phone", v)} />
                </FieldCell>
            </div>
            <div className={GRID_ROW_3}>
                <FieldCell
                    label={juridical.label}
                    flashing={flashes.includes(juridical.flatKey)}
                >
                    <Combobox
                        value={{ id: juridicalField.id ?? null, name: juridicalField.name ?? null }}
                        items={references[juridical.dictKey] ?? []}
                        onSelect={(item) => onDropdown(juridical.flatKey, item)}
                    />
                </FieldCell>
                <FieldCell
                    label={social.label}
                    flashing={flashes.includes(social.flatKey)}
                >
                    <Combobox
                        value={{ id: socialField.id ?? null, name: socialField.name ?? null }}
                        items={references[social.dictKey] ?? []}
                        onSelect={(item) => onDropdown(social.flatKey, item)}
                    />
                </FieldCell>
                <FieldCell label="Электронная почта">
                    <EditableClientInput value={card.client?.email ?? ""} onCommit={(v) => onClientText("email", v)} />
                </FieldCell>
            </div>
        </div>
    )
}


function MainTab({
    card,
    flashes,
    mainLocal,
    onMainLocalChange,
    onComments,
}: {
    card: VisitCardDTO
    flashes: string[]
    mainLocal: Record<string, string>
    onMainLocalChange: (flatKey: string, value: string) => void
    onComments: (value: string) => void
}) {
    const byKey = (flatKey: string) => MAIN_TEXT_FIELDS.find((s) => s.flatKey === flatKey)!

    return (
        <div className="space-y-2">
            <div className={GRID_ROW_3}>
                <MainTextFieldCell
                    spec={byKey("designer")}
                    card={card}
                    flashes={flashes}
                    mainLocal={mainLocal}
                    onMainLocalChange={onMainLocalChange}
                />
                <MainTextFieldCell
                    spec={byKey("salon")}
                    card={card}
                    flashes={flashes}
                    mainLocal={mainLocal}
                    onMainLocalChange={onMainLocalChange}
                />
                <MainTextFieldCell
                    spec={byKey("dealer")}
                    card={card}
                    flashes={flashes}
                    mainLocal={mainLocal}
                    onMainLocalChange={onMainLocalChange}
                />
            </div>
            <div className={GRID_ROW_3}>
                <MainTextFieldCell
                    spec={byKey("ad_source")}
                    card={card}
                    flashes={flashes}
                    mainLocal={mainLocal}
                    onMainLocalChange={onMainLocalChange}
                />
                <MainTextFieldCell
                    spec={byKey("brand")}
                    card={card}
                    flashes={flashes}
                    mainLocal={mainLocal}
                    onMainLocalChange={onMainLocalChange}
                />
                <MainTextFieldCell
                    spec={byKey("product_group")}
                    card={card}
                    flashes={flashes}
                    mainLocal={mainLocal}
                    onMainLocalChange={onMainLocalChange}
                />
            </div>
            <div className={GRID_ROW_2}>
                <MainTextFieldCell
                    spec={byKey("refusal_reason")}
                    card={card}
                    flashes={flashes}
                    mainLocal={mainLocal}
                    onMainLocalChange={onMainLocalChange}
                />
                <FieldCell label="Комментарий салона">
                    <Input
                        value={card.comments ?? ""}
                        onChange={(e) => onComments(e.target.value)}
                        placeholder="—"
                    />
                </FieldCell>
            </div>
        </div>
    )
}


function MainTextFieldCell({
    spec,
    card,
    flashes,
    mainLocal,
    onMainLocalChange,
}: {
    spec: MainTextFieldSpec
    card: VisitCardDTO
    flashes: string[]
    mainLocal: Record<string, string>
    onMainLocalChange: (flatKey: string, value: string) => void
}) {
    const field = spec.path ? (getField(card, spec.path) ?? EMPTY_FIELD) : null
    const hasLocalOverride = Object.prototype.hasOwnProperty.call(mainLocal, spec.flatKey)
    const cardText = field ? (field.name ?? field.raw ?? "") : ""
    const value = hasLocalOverride ? mainLocal[spec.flatKey] : cardText

    return (
        <FieldCell
            label={spec.label}
            flashing={flashes.includes(spec.flatKey)}
        >
            <Input
                value={value}
                onChange={(e) => onMainLocalChange(spec.flatKey, e.target.value)}
                placeholder="—"
            />
        </FieldCell>
    )
}


const EMPTY_FIELD: FieldValueDTO = {
    id: null,
    name: null,
    raw: null,
    confidence: 0,
    source: "unset",
    manual: false,
}


function EditableClientInput({
    value,
    onCommit,
}: {
    value: string
    onCommit: (value: string) => void
}) {
    const [local, setLocal] = React.useState(value)
    const focusedRef = React.useRef(false)
    React.useEffect(() => {
        if (!focusedRef.current) setLocal(value)
    }, [value])
    return (
        <Input
            value={local}
            placeholder="—"
            onChange={(e) => setLocal(e.target.value)}
            onFocus={() => {
                focusedRef.current = true
            }}
            onBlur={() => {
                focusedRef.current = false
                if (local !== value) onCommit(local)
            }}
        />
    )
}


function FieldCell({
    label,
    flashing,
    children,
}: {
    label: string
    flashing?: boolean
    children: React.ReactNode
}) {
    return (
        <div className={cn("min-w-0 space-y-0.5", flashing && "animate-pulse")}>
            <Label
                className="truncate text-caption font-regular text-neutral-text-500"
                title={label}
            >
                {label}
            </Label>
            {children}
        </div>
    )
}
